import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon,
  UserIcon,
  DocumentTextIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  PencilIcon,
  XMarkIcon,
  LanguageIcon,
  TruckIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import googleMapsLoader from '../utils/googleMapsLoader';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';
import {
  getMinimumReserveHours,
  resolveReserveHours,
  resolveServiceTypeForReserveRules,
} from '../utils/reserveHoursUtils';
import { 
  getJobStatusLabel,
  getJobStatusColor,
  getCustomerStatusMessage,
  isCancelledUnder24Hours,
  mapStatusForCustomer,
  canReRequestJob 
} from '../utils/statusConstants';
import JobChatWidget from '../components/JobChatWidget';
import { useChat } from '../contexts/ChatContext';
import ServiceLocationSelector from '../components/ServiceLocationSelector';
import TransportationRouteMap from '../components/TransportationRouteMap';
import { navigateBack } from '../components/RoutePersistence';
import AdjusterConfirmModal from '../components/AdjusterConfirmModal';
import { useViewExistingClaim } from '../hooks/useViewExistingClaim';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Service type options (same as NewAppointment.js)
const serviceTypeOptions = [
  { value: 'onsite', label: 'On-Site' },
  { value: 'video', label: 'Video Remote Interpretation (Zoom)' },
  { value: 'phone', label: 'Telehealth' },
  { value: 'phone_conference', label: 'Phone Conference' },
  { value: 'document', label: 'Document' }
];

// Appointment type options (same as NewAppointment.js)
const appointmentTypeOptions = [
  { value: 'acupuncture', label: 'Acupuncture' },
  { value: 'ame', label: 'AME' },
  { value: 'aoe_coe', label: 'AOE/COE' },
  { value: 'benefit_meeting', label: 'Benefit Meeting' },
  { value: 'cardiac_evaluation', label: 'Cardiac Evaluation' },
  { value: 'chiropractor', label: 'Chiropractor' },
  { value: 'consult_treat', label: 'Consult & Treat' },
  { value: 'consult_diagnostic_testing', label: 'Consult + Diagnostic Testing' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'ct_scan', label: 'Ct Scan' },
  { value: 'dentist', label: 'Dentist' },
  { value: 'deposition', label: 'Deposition' },
  { value: 'deposition_prep', label: 'Deposition Prep' },
  { value: 'diagnostic_testing', label: 'Diagnostic Testing' },
  { value: 'ekg', label: 'EKG' },
  { value: 'emg', label: 'EMG' },
  { value: 'employee_meeting', label: 'Employee Meeting' },
  { value: 'epidural_injection', label: 'Epidural Injection' },
  { value: 'equipment_explination', label: 'Equipment Explination' },
  { value: 'evaluation', label: 'Evaluation' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'follow_up_lab_work', label: 'Follow Up & Lab Work' },
  { value: 'follow_up_pt', label: 'Follow Up & PT' },
  { value: 'functional_capacity', label: 'Functional Capacity' },
  { value: 'hearing_loss', label: 'Hearing Loss' },
  { value: 'hernia_consult', label: 'Hernia Consult' },
  { value: 'ime', label: 'IME' },
  { value: 'initial_appointment', label: 'Initial Appointment' },
  { value: 'injection', label: 'Injection' },
  { value: 'injection_steroid', label: 'Injection Steroid' },
  { value: 'internal_medicine', label: 'Internal Medicine' },
  { value: 'interview', label: 'Interview' },
  { value: 'interview_witness', label: 'Interview Witness' },
  { value: 'laser_procedure', label: 'Laser Procedure' },
  { value: 'legal', label: 'Legal' },
  { value: 'massage', label: 'Massage' },
  { value: 'medical', label: 'Medical' },
  { value: 'medical_clerance', label: 'Medical Clerance' },
  { value: 'medical_procedure', label: 'Medical Procedure' },
  { value: 'mri', label: 'MRI' },
  { value: 'ncm_meeting', label: 'NCM Meeting' },
  { value: 'occupational', label: 'Occupational' },
  { value: 'one_time_appointment', label: 'One-Time Appointment' },
  { value: 'orthopaedic', label: 'Orthopaedic' },
  { value: 'pain_management', label: 'Pain Management' },
  { value: 'physical_therapy', label: 'Physical Therapy' },
  { value: 'physical_therapy_initial', label: 'Physical Therapy Initial' },
  { value: 'psychological', label: 'Psychological' },
  { value: 'psych_qme', label: 'Psych QME' },
  { value: 'qme', label: 'QME' },
  { value: 'recorded_statement', label: 'Recorded Statement' },
  { value: 'second_opinion', label: 'Second Opinion' },
  { value: 'settlement_documents', label: 'Settlement Documents' },
  { value: 'status_conference', label: 'Status Conference' },
  { value: 'surgery', label: 'Surgery' },
  { value: 'surgery_check_in', label: 'Surgery Check In' },
  { value: 'surgery_discharge', label: 'Surgery Discharge' },
  { value: 'surgery_post_op', label: 'Surgery Post-Op' },
  { value: 'surgery_pre_op', label: 'Surgery Pre-Op' },
  { value: 'surgical_consult', label: 'Surgical Consult' },
  { value: 'trial', label: 'Trial' },
  { value: 'vocational_evaluation', label: 'Vocational Evaluation' },
  { value: 'x_ray', label: 'X-Ray' },
  { value: 'mandatory_settlement_agreement', label: 'Mandatory Settlement Agreement' },
  { value: 'hearing', label: 'Hearing' },
  { value: 'mandatory_settlement_conference', label: 'Mandatory Settlement Conference' },
  { value: 'permanent_stationary', label: 'Permanent & Stationary' },
  { value: 'cognitive_behavioral_therapy', label: 'Cognitive behavioral therapy' },
  { value: 'speech_therapy_evaluation', label: 'Speech Therapy Evaluation' },
  { value: 'speech_therapy_follow_up', label: 'Speech Therapy Follow Up' },
  { value: 'other', label: 'Other' }
];

// Appointment type categorization for mapping logic (same as NewAppointment.js)
const legalAppointmentTypes = [
  'deposition', 'deposition_prep', 'legal', 'recorded_statement', 'settlement_documents',
  'status_conference', 'trial', 'mandatory_settlement_agreement',
  'hearing', 'mandatory_settlement_conference', 'permanent_stationary'
];

const medicalLegalAppointmentTypes = ['ame', 'ime', 'qme', 'psych_qme', 'aoe_coe', 'psychological', 'permanent_stationary'];

const medicalStandardAppointmentTypes = [
  'acupuncture', 'cardiac_evaluation', 'chiropractor', 'consult_treat', 'consult_diagnostic_testing', 'consultation',
  'ct_scan', 'dentist', 'diagnostic_testing', 'ekg', 'emg', 'epidural_injection', 'evaluation',
  'follow_up', 'follow_up_lab_work', 'follow_up_pt', 'functional_capacity', 'hearing_loss',
  'hernia_consult', 'initial_appointment', 'injection', 'injection_steroid', 'internal_medicine',
  'laser_procedure', 'massage', 'medical', 'medical_clerance', 'medical_procedure', 'mri', 'occupational',
  'one_time_appointment', 'orthopaedic', 'pain_management', 'physical_therapy', 'physical_therapy_initial',
  'psychological', 'second_opinion', 'surgery', 'surgery_check_in', 'surgery_discharge',
  'surgery_post_op', 'surgery_pre_op', 'surgical_consult', 'vocational_evaluation', 'x_ray',
  'cognitive_behavioral_therapy', 'speech_therapy_evaluation', 'speech_therapy_follow_up'
];

// Helper function to determine appointment category
const getAppointmentCategory = (appointmentType) => {
  if (legalAppointmentTypes.includes(appointmentType)) return 'legal';
  if (medicalLegalAppointmentTypes.includes(appointmentType)) return 'medical-legal';
  if (medicalStandardAppointmentTypes.includes(appointmentType)) return 'medical-standard';
  return 'other';
};

// Helper function to map frontend service type to backend service_type_id (same as NewAppointment.js)
const mapToBackendServiceType = (frontendServiceType, appointmentType, interpreterTypeCode, formOptions) => {
  // For non-onsite service types, map directly
  if (frontendServiceType === 'video') {
    return formOptions.serviceTypes?.find(st => st.code === 'video')?.id;
  }
  if (frontendServiceType === 'phone') {
    return formOptions.serviceTypes?.find(st => st.code === 'phone')?.id;
  }
  if (frontendServiceType === 'phone_conference') {
    return formOptions.serviceTypes?.find(st => st.code === 'phone_conference')?.id;
  }
  if (frontendServiceType === 'document') {
    return formOptions.serviceTypes?.find(st => st.code === 'document')?.id;
  }
  
  // For onsite, determine based on appointment type and interpreter type
  if (frontendServiceType === 'onsite') {
    const category = getAppointmentCategory(appointmentType);
    
    if (category === 'legal') {
      // Legal appointments always map to 'legal' service type
      return formOptions.serviceTypes?.find(st => st.code === 'legal')?.id;
    }
    
    if (category === 'medical-legal') {
      // Medical Certified appointments always map to 'medical' service type
      return formOptions.serviceTypes?.find(st => st.code === 'medical')?.id;
    }
    
    if (category === 'medical-standard') {
      // Medical Standard depends on selected interpreter type
      if (interpreterTypeCode === 'qualified_standard') {
        return formOptions.serviceTypes?.find(st => st.code === 'medical_non_legal')?.id;
      }
      if (interpreterTypeCode === 'court_certified') {
        return formOptions.serviceTypes?.find(st => st.code === 'legal')?.id;
      }
      if (interpreterTypeCode === 'medical_certified') {
        return formOptions.serviceTypes?.find(st => st.code === 'medical')?.id;
      }
    }
  }
  
  return null;
};

// Helper function to map backend service_type_id to frontend service type
const mapFromBackendServiceType = (serviceTypeId, serviceTypes) => {
  if (!serviceTypeId || !serviceTypes || serviceTypes.length === 0) return '';
  
  const serviceType = serviceTypes.find(st => st.id.toString() === serviceTypeId.toString());
  if (!serviceType) return '';
  
  const code = serviceType.code;
  if (code === 'video') return 'video';
  if (code === 'phone') return 'phone';
  if (code === 'phone_conference') return 'phone_conference';
  if (code === 'document') return 'document';
  // For legal, medical, medical_non_legal, all map to 'onsite'
  return 'onsite';
};

const formatCertificationDetails = (certification, fallback = 'Certification details unavailable') => {
  if (!certification) return fallback;
  
  const parts = [];
  if (certification.certificate_number) {
    parts.push(`Certificate #${certification.certificate_number}`);
  }
  if (certification.issuing_organization) {
    parts.push(certification.issuing_organization);
  }
  if (!parts.length && certification.type) {
    parts.push(certification.type);
  }
  
  return parts.join(' • ') || fallback;
};

const AppointmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { makeAuthenticatedRequest, customer } = useAuth();
  const { conversations, unreadByConversation } = useChat();
  const {
    promptIfNeeded,
    pendingClaim,
    saveAssignment,
    skipPrompt,
    saving: savingRole
  } = useViewExistingClaim(makeAuthenticatedRequest, customer);
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [mapsInitialized, setMapsInitialized] = useState(false);
  const [totalMiles, setTotalMiles] = useState(null);
  const [calculatingMiles, setCalculatingMiles] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [locationContacts, setLocationContacts] = useState([]);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactFormData, setNewContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    title: ''
  });
  const [formOptions, setFormOptions] = useState({
    interpreterTypes: [],
    serviceTypes: []
  });
  const [isEditingTransportationLocations, setIsEditingTransportationLocations] = useState(false);
  const [editingTransportationLocations, setEditingTransportationLocations] = useState([]);
  const [serviceLocations, setServiceLocations] = useState([]);
  const [selectedServiceLocations, setSelectedServiceLocations] = useState({}); // Map of index -> service location object
  const [relatedAppointments, setRelatedAppointments] = useState([]);
  const [loadingRelatedAppointments, setLoadingRelatedAppointments] = useState(false);

  const loadAppointmentDetails = useCallback(async () => {
    try {
      setLoading(true);
      
      const data = await makeAuthenticatedRequest(`/appointments/${id}`);
      
      if (data.success) {
        setAppointment(data.data);
        // Load related appointments after loading appointment details
        loadRelatedAppointments();
      } else {
        toast.error(data.message || 'Failed to load appointment details');
        navigate('/appointments');
      }
    } catch (error) {
      console.error('Error loading appointment details:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load appointment details'));
      navigate('/appointments');
    } finally {
      setLoading(false);
    }
  }, [id, makeAuthenticatedRequest, navigate]);

  // Load related appointments
  const loadRelatedAppointments = useCallback(async () => {
    try {
      setLoadingRelatedAppointments(true);
      const data = await makeAuthenticatedRequest(`/appointments/${id}/related-appointments`);
      if (data.success) {
        setRelatedAppointments(data.data || []);
      }
    } catch (error) {
      console.error('Error loading related appointments:', error);
    } finally {
      setLoadingRelatedAppointments(false);
    }
  }, [id, makeAuthenticatedRequest]);

  useEffect(() => {
    loadAppointmentDetails();
  }, [loadAppointmentDetails]);

  useEffect(() => {
    const claimId = appointment?.claim_id;
    if (!claimId || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await makeAuthenticatedRequest(`/claims/${claimId}`);
        if (!cancelled && data?.success && data.data) {
          promptIfNeeded(data.data, { onUpdated: loadAppointmentDetails });
        }
      } catch (e) {
        // Prompt is optional if the claim cannot be loaded
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointment?.claim_id, loading]);

  const initializeGoogleMaps = async () => {
    try {
      await googleMapsLoader.load(['geometry']);
      setMapsInitialized(true);
    } catch (error) {
      console.error('Failed to load Google Maps API:', error);
    }
  };

  // Calculate total mileage
  const calculateTotalMiles = useCallback(async (showToast = false) => {
    if (!mapsInitialized || !window.google || !window.google.maps) {
      if (showToast) {
        toast.error('Google Maps not available for mileage calculation');
      }
      return;
    }

    setCalculatingMiles(true);

    try {
      console.log('Calculating mileage for appointment:', appointment);
      console.log('Transportation locations:', appointment.transportation_locations);
      console.log('Pickup location:', appointment.pickup_location);
      console.log('Dropoff location:', appointment.dropoff_location);
      console.log('Second dropoff location:', appointment.second_dropoff_location);

      const locations = [];
      const locationDetails = [];

      // Handle new multi-trip format first
      if (appointment.transportation_locations && appointment.transportation_locations.length > 0) {
        console.log('Using new multi-trip format with', appointment.transportation_locations.length, 'locations');
        
        // Sort locations by leg_sequence to ensure correct order
        const sortedLocations = appointment.transportation_locations.sort((a, b) => a.leg_sequence - b.leg_sequence);
        
        for (const location of sortedLocations) {
          if (location.address) {
            const completeAddress = buildCompleteAddress(location);
            if (location.latitude && location.longitude) {
              // Use existing coordinates
              locations.push(new window.google.maps.LatLng(
                location.latitude,
                location.longitude
              ));
              locationDetails.push(completeAddress);
              console.log(`Added ${location.location_type} location with coordinates:`, completeAddress);
            } else {
              // Geocode the address to get coordinates
              try {
                const coords = await geocodeAddress(completeAddress);
                if (coords) {
                  locations.push(new window.google.maps.LatLng(coords.lat, coords.lng));
                  locationDetails.push(completeAddress);
                  console.log(`Added ${location.location_type} location via geocoding:`, completeAddress);
                }
              } catch (error) {
                console.warn(`Failed to geocode ${location.location_type} location:`, error);
              }
            }
          }
        }
      } else {
        // Fallback to legacy format
        console.log('Using legacy format');
        
        // Add pickup location
        if (appointment.pickup_location && appointment.pickup_location.address) {
          const completeAddress = buildCompleteAddress(appointment.pickup_location);
          if (appointment.pickup_location.latitude && appointment.pickup_location.longitude) {
            // Use existing coordinates
            locations.push(new window.google.maps.LatLng(
              appointment.pickup_location.latitude,
              appointment.pickup_location.longitude
            ));
            locationDetails.push(completeAddress);
            console.log('Added pickup location with coordinates:', completeAddress);
          } else {
            // Geocode the address to get coordinates
            try {
              const pickupCoords = await geocodeAddress(completeAddress);
              if (pickupCoords) {
                locations.push(new window.google.maps.LatLng(pickupCoords.lat, pickupCoords.lng));
                locationDetails.push(completeAddress);
                console.log('Added pickup location via geocoding:', completeAddress);
              }
            } catch (error) {
              console.warn('Failed to geocode pickup location:', error);
            }
          }
        }

        // Add dropoff location
        if (appointment.dropoff_location && appointment.dropoff_location.address) {
          const completeAddress = buildCompleteAddress(appointment.dropoff_location);
          if (appointment.dropoff_location.latitude && appointment.dropoff_location.longitude) {
            // Use existing coordinates
            locations.push(new window.google.maps.LatLng(
              appointment.dropoff_location.latitude,
              appointment.dropoff_location.longitude
            ));
            locationDetails.push(completeAddress);
            console.log('Added dropoff location with coordinates:', completeAddress);
          } else {
            // Geocode the address to get coordinates
            try {
              const dropoffCoords = await geocodeAddress(completeAddress);
              if (dropoffCoords) {
                locations.push(new window.google.maps.LatLng(dropoffCoords.lat, dropoffCoords.lng));
                locationDetails.push(completeAddress);
                console.log('Added dropoff location via geocoding:', completeAddress);
              }
            } catch (error) {
              console.warn('Failed to geocode dropoff location:', error);
            }
          }
        }

        // Add second dropoff location if it exists
        if (appointment.second_dropoff_location && appointment.second_dropoff_location.address) {
          const completeAddress = buildCompleteAddress(appointment.second_dropoff_location);
          if (appointment.second_dropoff_location.latitude && appointment.second_dropoff_location.longitude) {
            // Use existing coordinates
            locations.push(new window.google.maps.LatLng(
              appointment.second_dropoff_location.latitude,
              appointment.second_dropoff_location.longitude
            ));
            locationDetails.push(completeAddress);
            console.log('Added second dropoff location with coordinates:', completeAddress);
          } else {
            // Geocode the address to get coordinates
            try {
              const secondDropoffCoords = await geocodeAddress(completeAddress);
              if (secondDropoffCoords) {
                locations.push(new window.google.maps.LatLng(secondDropoffCoords.lat, secondDropoffCoords.lng));
                locationDetails.push(completeAddress);
                console.log('Added second dropoff location via geocoding:', completeAddress);
              }
            } catch (error) {
              console.warn('Failed to geocode second dropoff location:', error);
            }
          }
        }
      }

      console.log('Total locations found:', locations.length);
      console.log('Location details:', locationDetails);

      if (locations.length < 2) {
        console.log('Not enough locations for mileage calculation');
        if (showToast) {
          toast.error('Need at least 2 locations to calculate mileage');
        }
        return;
      }

      // Calculate driving distance using Directions API
      let totalDrivingDistance = 0;
      
      for (let i = 0; i < locations.length - 1; i++) {
        try {
          const distance = await getDrivingDistance(locations[i], locations[i + 1]);
          totalDrivingDistance += distance;
        } catch (error) {
          console.warn(`Failed to calculate driving distance for segment ${i}:`, error);
        }
      }

      const drivingMilesTotal = (totalDrivingDistance * 0.000621371).toFixed(2);
      const drivingMilesRounded = Math.ceil(parseFloat(drivingMilesTotal)); // Round up to nearest integer
      setTotalMiles(drivingMilesRounded);
      
      if (showToast) {
        toast.success(`Mileage calculated: ${drivingMilesRounded} miles (driving distance, rounded up)`);
      }
      
    } catch (error) {
      console.error('Error calculating mileage:', error);
      if (showToast) {
        toast.error(getCustomerPortalErrorMessage(error, 'Failed to calculate mileage'));
      }
    } finally {
      setCalculatingMiles(false);
    }
  }, [mapsInitialized, appointment]);

  // Build complete address string from location object
  // Matches the backend logic to avoid duplicate/conflicting data
  const buildCompleteAddress = (location) => {
    if (!location) return '';
    
    // If the address field already contains a complete formatted address (with zip code),
    // use it as-is to avoid sending duplicate/conflicting location data to Google Maps API
    if (location.address) {
      // Check if address already contains a zip code pattern (5-digit number)
      const hasZipCode = /\b\d{5}\b/.test(location.address);
      
      // If address already has a zip code, it's likely a complete formatted address
      if (hasZipCode) {
        return location.address;
      }
    }
    
    // Otherwise, build the address from individual components
    const parts = [];
    if (location.address) parts.push(location.address);
    if (location.city) parts.push(location.city);
    if (location.state) parts.push(location.state);
    if (location.zip_code) parts.push(location.zip_code);
    return parts.join(', ');
  };

  // Helper function to geocode an address to get coordinates
  const geocodeAddress = (address) => {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng()
          });
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  };

  // Helper function to get driving distance between two points
  const getDrivingDistance = (origin, destination) => {
    return new Promise((resolve, reject) => {
      const directionsService = new window.google.maps.DirectionsService();
      
      directionsService.route({
        origin: origin,
        destination: destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL
      }, (result, status) => {
        if (status === 'OK') {
          const route = result.routes[0];
          let totalDistance = 0;
          
          route.legs.forEach(leg => {
            totalDistance += leg.distance.value; // Distance in meters
          });
          
          resolve(totalDistance);
        } else {
          reject(new Error(`Directions request failed: ${status}`));
        }
      });
    });
  };

  // Initialize Google Maps
  useEffect(() => {
    initializeGoogleMaps();
  }, []);

  // Auto-calculate mileage when locations are available
  useEffect(() => {
    if (appointment && appointment.job_type === 'transportation') {
      // Use calculated_mileage from backend if available, otherwise calculate it
      if (appointment.calculated_mileage) {
        setTotalMiles(Math.ceil(appointment.calculated_mileage));
      } else if (mapsInitialized && (appointment.pickup_location || appointment.dropoff_location)) {
        calculateTotalMiles();
      }
    }
  }, [mapsInitialized, appointment, calculateTotalMiles]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    
    try {
      // Extract date parts to avoid timezone conversion
      // If date is in format "2025-10-24T00:00:00.000Z" or "2025-10-24", extract just the date part
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      
      // Create date in local timezone (no UTC conversion)
      const localDate = new Date(year, month - 1, day);
      
      // Check if the date is valid
      if (isNaN(localDate.getTime())) {
        return 'Invalid Date';
      }
      
      return localDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // SearchableSelect component (same as NewAppointment.js)
  const SearchableSelect = ({ 
    label, 
    value, 
    onChange, 
    options = [], 
    placeholder = "Search...", 
    className = '', 
    required = false, 
    disabled = false,
    onClear 
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Set search term when value changes (for external updates)
    useEffect(() => {
      if (value) {
        const selectedOption = options.find(option => option.value === value);
        if (selectedOption) {
          setSearchTerm(selectedOption.label);
        }
      } else {
        setSearchTerm('');
      }
    }, [value, options]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleSelect = (option) => {
      if (option && option.value !== undefined) {
        onChange({ target: { value: option.value } });
        setSearchTerm(option.label || '');
        setIsOpen(false);
      }
    };

    const handleInputChange = (e) => {
      setSearchTerm(e.target.value);
      setIsOpen(true);
    };

    const handleClear = () => {
      setSearchTerm('');
      onChange({ target: { value: '' } });
      if (onClear) {
        onClear();
      }
    };

    const showClearButton = value && !disabled;

    return (
      <div className={`relative searchable-select ${className}`} ref={dropdownRef}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        )}
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${showClearButton ? 'pr-16' : 'pr-10'} ${disabled ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {showClearButton && (
              <button
                type="button"
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 mr-2"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 pointer-events-none" />
          </div>
          {isOpen && filteredOptions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
              {filteredOptions.map((option, index) => (
                <div
                  key={index}
                  onClick={() => handleSelect(option)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };


  // Check if appointment can be edited
  const canEditAppointment = (appointment) => {
    if (!appointment) return false;
    const nonEditableStatuses = ['completed', 'billed', 'closed', 'interpreter_paid', 'cancelled', 'no_show'];
    const canEdit = !nonEditableStatuses.includes(appointment.status);
    console.log('Can edit appointment:', canEdit, 'Status:', appointment.status);
    return canEdit;
  };

  // Check if appointment can be authorized
  const canAuthorizeAppointment = (appointment) => {
    if (!appointment) return false;
    // Check if authorization is required, not yet obtained, and current customer can authorize
    return appointment.customer_authorization_required && 
           !appointment.customer_authorization_obtained && 
           appointment.can_authorize;
  };

  // Handle authorization
  const handleAuthorizeAppointment = async () => {
    if (!window.confirm('Are you sure you want to authorize this appointment?')) {
      return;
    }

    try {
      setSaving(true);
      
      const response = await makeAuthenticatedRequest(`/appointments/${id}/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          approved: true
        })
      });

      if (response.success) {
        // Check if already authorized
        if (response.alreadyAuthorized) {
          toast.success(response.message);
        } else {
          toast.success('Appointment authorized successfully!');
        }
        // Reload appointment details to show updated status
        await loadAppointmentDetails();
      } else {
        toast.error(response.message || 'Failed to authorize appointment');
      }
    } catch (error) {
      console.error('Error authorizing appointment:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to authorize appointment'));
    } finally {
      setSaving(false);
    }
  };

  // Handle transportation authorization
  const handleAuthorizeTransportation = async () => {
    if (!window.confirm('Are you sure you want to authorize this transportation service? This will allow us to proceed with the scheduled transportation.')) {
      return;
    }

    try {
      setSaving(true);
      
      const response = await makeAuthenticatedRequest(`${API_BASE}/customer/transportation-jobs/${id}/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          authorized: true
        })
      });

      if (response.success) {
        // Check if already authorized
        if (response.alreadyAuthorized) {
          toast.success(response.message);
        } else {
          toast.success('Transportation authorized successfully!');
        }
        // Reload appointment details to show updated status
        await loadAppointmentDetails();
      } else {
        toast.error(response.message || 'Failed to authorize transportation');
      }
    } catch (error) {
      console.error('Error authorizing transportation:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to authorize transportation'));
    } finally {
      setSaving(false);
    }
  };

  // Handle transportation decline
  const handleDeclineTransportation = async () => {
    const reason = prompt('Please provide a reason for declining this transportation service:');
    
    if (!reason || !reason.trim()) {
      toast.error('A reason is required to decline');
      return;
    }

    try {
      setSaving(true);
      
      const response = await makeAuthenticatedRequest(`${API_BASE}/customer/transportation-jobs/${id}/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          authorized: false,
          declineReason: reason
        })
      });

      if (response.success) {
        toast.success('Transportation declined. We will find an alternative solution.');
        // Reload appointment details to show updated status
        await loadAppointmentDetails();
      } else {
        toast.error(response.message || 'Failed to decline transportation');
      }
    } catch (error) {
      console.error('Error declining transportation:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to decline transportation'));
    } finally {
      setSaving(false);
    }
  };

  // Helper function to format date for HTML date input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // If the date is already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Otherwise parse and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  };

  // Helper function to calculate reserve hours from duration minutes
  const calculateReserveHours = (durationMinutes) => {
    if (!durationMinutes) return '';
    const hours = durationMinutes / 60;
    return Math.round(hours * 2) / 2; // Round to nearest 0.5 hour
  };

  // Helper function to calculate end time from start time and reserve hours
  const calculateEndTime = (startTime, reserveHours) => {
    if (!startTime || !reserveHours) return '';
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(start.getTime() + (reserveHours * 60 * 60 * 1000));
    return end.toTimeString().slice(0, 5); // Return HH:MM format
  };

  // Load form options (interpreter types and service types)
  const loadFormOptions = useCallback(async () => {
    try {
      const [interpreterTypesData, serviceTypesData] = await Promise.all([
        makeAuthenticatedRequest('/interpreter-types'),
        makeAuthenticatedRequest('/parametric/service-types')
      ]);
      const newFormOptions = {
        interpreterTypes: interpreterTypesData.success ? interpreterTypesData.data : [],
        serviceTypes: serviceTypesData.success ? serviceTypesData.data : []
      };
      setFormOptions(newFormOptions);
      return newFormOptions;
    } catch (error) {
      console.error('Error loading form options:', error);
      return {
        interpreterTypes: [],
        serviceTypes: []
      };
    }
  }, [makeAuthenticatedRequest]);

  // Start editing
  const startEditing = async () => {
    const locationContactId = appointment.location_contact_id ? appointment.location_contact_id.toString() : '';
    
    // Load form options if not already loaded
    let currentFormOptions = formOptions;
    if (formOptions.interpreterTypes.length === 0 || formOptions.serviceTypes.length === 0) {
      currentFormOptions = await loadFormOptions();
    }
    
    // Map backend service type to frontend service type
    const mappedServiceType = mapFromBackendServiceType(
      appointment.service_type_id,
      currentFormOptions.serviceTypes || []
    );
    
    setEditData({
      appointmentDate: formatDateForInput(appointment.scheduled_date),
      startTime: appointment.scheduled_time || '',
      arrivalTime: appointment.arrival_time 
        ? appointment.arrival_time.substring(0, 5) 
        : appointment.scheduled_time 
          ? appointment.scheduled_time.substring(0, 5) 
          : '',
      reserveHours: calculateReserveHours(appointment.estimated_duration_minutes),
      // Pre-fill service location
      serviceLocationId: appointment.service_location_id || '',
      selectedServiceLocation: null, // Will be loaded below
      locationContactId: locationContactId,
      appointmentType: appointment.appointment_type || '',
      appointmentNotes: appointment.appointment_notes || '',
      interpreterType: appointment.interpreter_type_id ? appointment.interpreter_type_id.toString() : '',
      serviceType: mappedServiceType,
      meetingLink: appointment.meeting_link || ''
    });
    
    // Load service location details if it exists
    if (appointment.service_location_id) {
      try {
        const locationResponse = await makeAuthenticatedRequest(`/service-locations/${appointment.service_location_id}`);
        if (locationResponse.success && locationResponse.data) {
          const location = locationResponse.data;
          setEditData(prev => ({
            ...prev,
            selectedServiceLocation: location
          }));
          
          // Load location contacts for this service location
          await loadLocationContacts(appointment.service_location_id);
          
          // Re-set the location contact ID after loading contacts
          setEditData(prev => ({
            ...prev,
            locationContactId: locationContactId
          }));
        }
      } catch (error) {
        console.error('Error loading service location:', error);
      }
    }
    
    setIsEditing(true);
  };

  // Load location contacts for a service location
  const loadLocationContacts = async (serviceLocationId) => {
    try {
      const response = await makeAuthenticatedRequest(`/location-contacts?service_location_id=${serviceLocationId}`);
      if (response.success) {
        setLocationContacts(response.data || []);
      } else {
        setLocationContacts([]);
      }
    } catch (error) {
      console.error('Error loading location contacts:', error);
      setLocationContacts([]);
    }
  };

  // Handle service location selection
  const handleServiceLocationSelect = async (location) => {
    if (location) {
      setEditData(prev => ({
        ...prev,
        selectedServiceLocation: location,
        serviceLocationId: location.id,
        locationContactId: '' // Reset location contact when location changes
      }));
      
      // Load location contacts for this service location
      await loadLocationContacts(location.id);
    } else {
      setEditData(prev => ({
        ...prev,
        selectedServiceLocation: null,
        serviceLocationId: '',
        locationContactId: ''
      }));
      setLocationContacts([]);
    }
  };

  // Handle location contact selection
  const handleLocationContactSelect = (contactId) => {
    setEditData(prev => ({
      ...prev,
      locationContactId: contactId
    }));
  };

  // Create new location contact
  const handleCreateLocationContact = async (e) => {
    e.preventDefault();
    
    if (!newContactFormData.name || !editData.serviceLocationId) {
      toast.error('Name and service location are required');
      return;
    }

    // Validate service_location_id is a valid integer
    const serviceLocationId = parseInt(editData.serviceLocationId);
    if (isNaN(serviceLocationId) || serviceLocationId <= 0) {
      toast.error('Invalid service location ID');
      return;
    }

    // Build request body - only include optional fields if they have values
    const requestBody = {
      service_location_id: serviceLocationId,
      name: newContactFormData.name.trim()
    };

    // Only add optional fields if they have values
    if (newContactFormData.email && newContactFormData.email.trim()) {
      requestBody.email = newContactFormData.email.trim();
    }
    if (newContactFormData.phone && newContactFormData.phone.trim()) {
      requestBody.phone = newContactFormData.phone.trim();
    }
    if (newContactFormData.title && newContactFormData.title.trim()) {
      requestBody.title = newContactFormData.title.trim();
    }

    try {
      const response = await makeAuthenticatedRequest('/location-contacts', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (response.success) {
        toast.success('Location contact created successfully');
        setShowNewContactModal(false);
        setNewContactFormData({
          name: '',
          email: '',
          phone: '',
          title: ''
        });
        // Reload location contacts and auto-select the new one
        await loadLocationContacts(editData.serviceLocationId);
        setEditData(prev => ({
          ...prev,
          locationContactId: response.data.id.toString()
        }));
      } else {
        toast.error(response.message || 'Failed to create location contact');
      }
    } catch (error) {
      console.error('Error creating location contact:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to create location contact'));
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditData({});
    setLocationContacts([]);
  };

  // Save changes
  const saveChanges = async () => {
    try {
      setSaving(true);
      
      // Validate required fields
      if (!editData.appointmentDate || !editData.startTime || !editData.arrivalTime) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      // Validate interpreter type if required (for medical-standard appointments)
      if (appointment.job_type !== 'transportation' && !appointment.is_remote) {
        const category = editData.appointmentType ? getAppointmentCategory(editData.appointmentType) : null;
        const needsInterpreterType = category === 'medical-standard';
        if (needsInterpreterType && !editData.interpreterType) {
          toast.error('Please select an interpreter type for medical-standard appointments');
          return;
        }
      }
      
      // Validate arrival time is before or equal to start time
      const arrival = new Date(`2000-01-01T${editData.arrivalTime}`);
      const start = new Date(`2000-01-01T${editData.startTime}`);
      if (arrival > start) {
        toast.error('Arrival time must be before or equal to start time');
        return;
      }

      // Validate reserve hours
      const serviceTypeKey = resolveServiceTypeForReserveRules(
        editData.serviceTypeId || appointment.service_type_id,
        formOptions.serviceTypes,
        editData.serviceType
      );
      const minReserveHours = getMinimumReserveHours(serviceTypeKey);
      const effectiveReserveHours = resolveReserveHours(editData.reserveHours, serviceTypeKey);
      if (effectiveReserveHours < minReserveHours) {
        toast.error(`Reserve hours must be at least ${minReserveHours} hours for this service type`);
        return;
      }
      
      if (effectiveReserveHours > 12) {
        toast.error('Reserve hours cannot exceed 12 hours');
        return;
      }

      // Calculate end time from start time and reserve hours
      const endTime = calculateEndTime(editData.startTime, effectiveReserveHours);

      // Prepare data - send all edited fields
      const appointmentData = {
        appointmentDate: editData.appointmentDate,
        startTime: editData.startTime,
        arrivalTime: editData.arrivalTime,
        endTime: endTime
      };
      
      // Add location and other fields if they were edited (only for non-transportation jobs)
      if (appointment.job_type !== 'transportation') {
        if (editData.serviceLocationId !== undefined) {
          appointmentData.serviceLocationId = editData.serviceLocationId;
        }
        if (editData.locationContactId !== undefined) {
          appointmentData.locationContactId = editData.locationContactId;
        }
        if (editData.appointmentType !== undefined) {
          appointmentData.appointmentType = editData.appointmentType;
        }
        if (editData.interpreterType !== undefined) {
          appointmentData.interpreterType = editData.interpreterType;
        }
        if (editData.serviceType !== undefined) {
          // Map frontend service type to backend service_type_id
          const interpreterTypeCode = editData.interpreterType ? 
            formOptions.interpreterTypes?.find(it => it.id.toString() === editData.interpreterType)?.code : null;
          const backendServiceTypeId = mapToBackendServiceType(
            editData.serviceType,
            editData.appointmentType || appointment.appointment_type,
            interpreterTypeCode,
            formOptions
          );
          if (backendServiceTypeId) {
            appointmentData.serviceTypeId = backendServiceTypeId;
          }
        }
        if (editData.appointmentNotes !== undefined) {
          appointmentData.appointmentNotes = editData.appointmentNotes;
        }
        if (editData.meetingLink !== undefined) {
          appointmentData.meetingLink = editData.meetingLink;
        }
      }
      
      const data = await makeAuthenticatedRequest(`/appointments/${id}`, {
        method: 'PUT',
        body: JSON.stringify(appointmentData)
      });
      
      if (data.success) {
        // Reload appointment details to get the complete updated data including location info
        await loadAppointmentDetails();
        setIsEditing(false);
        setEditData({});
        setLocationContacts([]);
        toast.success('Appointment updated successfully');
      } else {
        toast.error(data.message || 'Failed to update appointment');
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to update appointment'));
    } finally {
      setSaving(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Handle auto-population logic for interpreter type when appointment type changes
    if (field === 'appointmentType' && formOptions.interpreterTypes && formOptions.interpreterTypes.length > 0) {
      const appointmentType = value;
      
      if (appointmentType) {
        const category = getAppointmentCategory(appointmentType);
        let interpreterType = null;
        
        // Auto-populate for Legal appointments
        if (category === 'legal') {
          interpreterType = formOptions.interpreterTypes.find(it => it.code === 'court_certified');
          if (interpreterType) {
            setEditData(prev => ({
              ...prev,
              interpreterType: interpreterType.id.toString()
            }));
            toast.success(`Interpreter type automatically set to "${interpreterType.name}" for legal appointments`, {
              duration: 2000,
              position: 'top-right'
            });
          }
        }
        // Auto-populate for Medical Certified appointments
        else if (category === 'medical-legal') {
          interpreterType = formOptions.interpreterTypes.find(it => it.code === 'medical_certified');
          if (interpreterType) {
            setEditData(prev => ({
              ...prev,
              interpreterType: interpreterType.id.toString()
            }));
            toast.success(`Interpreter type automatically set to "${interpreterType.name}" for medical certified evaluations`, {
              duration: 2000,
              position: 'top-right'
            });
          }
        }
        // For Medical Standard, clear interpreter type so user can select
        else if (category === 'medical-standard') {
          setEditData(prev => ({
            ...prev,
            interpreterType: ''
          }));
        }
      }
    }
  };

  // Handle reserve hours input with better validation
  const handleReserveHoursChange = (value) => {
    const serviceTypeKey = resolveServiceTypeForReserveRules(
      editData.serviceTypeId || appointment?.service_type_id,
      formOptions.serviceTypes,
      editData.serviceType
    );
    const minHours = getMinimumReserveHours(serviceTypeKey);
    const numValue = parseFloat(value);
    if (isNaN(numValue) || value === '') {
      handleInputChange('reserveHours', '');
    } else {
      const roundedValue = Math.round(numValue * 2) / 2;
      handleInputChange('reserveHours', Math.max(roundedValue, minHours));
    }
  };

  // Cancel appointment
  const cancelAppointment = async () => {
    if (!window.confirm('Are you sure you want to cancel this appointment? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      
      const data = await makeAuthenticatedRequest(`/appointments/${id}/cancel`, {
        method: 'POST'
      });
      
      if (data.success) {
        setAppointment(data.data);
        toast.success(data.message || 'Appointment cancelled successfully');
      } else {
        toast.error(data.message || 'Failed to cancel appointment');
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to cancel appointment'));
    } finally {
      setSaving(false);
    }
  };

  // Load service locations
  const loadServiceLocations = async () => {
    try {
      const response = await makeAuthenticatedRequest('/service-locations');
      if (response.success) {
        const locations = response.data || [];
        setServiceLocations(locations);
        return locations; // Return locations for use in startEditingTransportationLocations
      }
      return [];
    } catch (error) {
      console.error('Error loading service locations:', error);
      return [];
    }
  };

  // Start editing transportation locations
  const startEditingTransportationLocations = async () => {
    console.log('=== START EDITING TRANSPORTATION LOCATIONS ===');
    console.log('1. Current appointment:', appointment);
    console.log('2. Transportation locations from appointment:', appointment?.transportation_locations);
    
    // Load service locations first and wait for them
    const loadedServiceLocations = await loadServiceLocations();
    console.log('3. Loaded service locations:', {
      count: loadedServiceLocations?.length || 0,
      firstFew: loadedServiceLocations?.slice(0, 3).map(sl => ({ id: sl.id, name: sl.name, address: sl.address }))
    });
    
    const locations = appointment.transportation_locations || [];
    console.log('4. Locations to process:', locations.length);
    
    // Log each location's details
    locations.forEach((loc, idx) => {
      console.log(`   Location ${idx}:`, {
        location_type: loc.location_type,
        leg_sequence: loc.leg_sequence,
        service_location_id: loc.service_location_id,
        service_location_id_type: typeof loc.service_location_id,
        address: loc.address,
        city: loc.city,
        state: loc.state,
        zip_code: loc.zip_code
      });
    });
    
    // Prepare initial locations data
    const initialLocations = locations.map((loc) => ({
      location_type: loc.location_type,
      leg_sequence: loc.leg_sequence,
      address: loc.address || '',
      city: loc.city || '',
      state: loc.state || '',
      zip_code: loc.zip_code || '',
      special_instructions: loc.special_instructions || '',
      service_location_id: loc.service_location_id || null
    }));
    
    console.log('5. Initial locations prepared:', initialLocations);
    
    // Find existing service locations from the loaded list
    // Use Promise.all to fetch all service locations in parallel if needed
    const selectedLocationsPromises = locations.map(async (loc, index) => {
      console.log(`\n6. Processing location ${index}:`, {
        service_location_id: loc.service_location_id,
        service_location_id_type: typeof loc.service_location_id,
        address: loc.address,
        city: loc.city,
        state: loc.state,
        zip_code: loc.zip_code
      });
      
      if (loc.service_location_id) {
        console.log(`   - Has service_location_id: ${loc.service_location_id} (type: ${typeof loc.service_location_id})`);
        
        // First try to find in the loaded service locations list
        console.log(`   - Searching in ${loadedServiceLocations.length} loaded service locations...`);
        const foundLocation = loadedServiceLocations.find(sl => {
          const slId = typeof sl.id === 'number' ? sl.id : parseInt(sl.id);
          const locId = typeof loc.service_location_id === 'number' ? loc.service_location_id : parseInt(loc.service_location_id);
          const match1 = slId === locId;
          const match2 = sl.id.toString() === loc.service_location_id.toString();
          const match3 = parseInt(sl.id) === parseInt(loc.service_location_id);
          
          console.log(`     Comparing: sl.id=${sl.id} (${typeof sl.id}) vs loc.service_location_id=${loc.service_location_id} (${typeof loc.service_location_id})`);
          console.log(`     Match checks: ${match1}, ${match2}, ${match3}`);
          
          return match1 || match2 || match3;
        });
        
        if (foundLocation) {
          console.log(`   ✓ Found in loaded list:`, {
            id: foundLocation.id,
            name: foundLocation.name,
            address: foundLocation.address
          });
          return { index, location: foundLocation };
        } else {
          console.log(`   ✗ Not found in loaded list, trying individual fetch...`);
          // If not found in list, try to fetch individually (might be inactive or not in list)
          try {
            const locationResponse = await makeAuthenticatedRequest(`/service-locations/${loc.service_location_id}`);
            console.log(`   Individual fetch response:`, {
              success: locationResponse.success,
              hasData: !!locationResponse.data,
              data: locationResponse.data ? { id: locationResponse.data.id, name: locationResponse.data.name } : null
            });
            if (locationResponse.success && locationResponse.data) {
              console.log(`   ✓ Successfully fetched individually`);
              return { index, location: locationResponse.data };
            }
          } catch (error) {
            console.error(`   ✗ Error loading service location:`, error);
          }
        }
      } else {
        // No service_location_id - try to match by address
        console.log(`   - No service_location_id, trying to match by address...`);
        if (loc.address) {
          // Normalize addresses for comparison (remove extra spaces, case insensitive)
          const normalizeAddress = (addr) => {
            if (!addr) return '';
            return addr.trim().toLowerCase().replace(/\s+/g, ' ');
          };
          
          const normalizedLocAddress = normalizeAddress(loc.address);
          const normalizedLocCity = normalizeAddress(loc.city || '');
          const normalizedLocState = normalizeAddress(loc.state || '');
          const normalizedLocZip = normalizeAddress(loc.zip_code || '');
          
          console.log(`   - Normalized location address: "${normalizedLocAddress}", city: "${normalizedLocCity}", state: "${normalizedLocState}", zip: "${normalizedLocZip}"`);
          
          // Try to find matching service location by address
          const foundByAddress = loadedServiceLocations.find(sl => {
            const normalizedSlAddress = normalizeAddress(sl.address || '');
            const normalizedSlCity = normalizeAddress(sl.city || '');
            const normalizedSlState = normalizeAddress(sl.state || '');
            const normalizedSlZip = normalizeAddress(sl.zip_code || '');
            
            // Match if address matches (and optionally city/state/zip if provided)
            const addressMatch = normalizedSlAddress === normalizedLocAddress;
            const cityMatch = !normalizedLocCity || normalizedSlCity === normalizedLocCity;
            const stateMatch = !normalizedLocState || normalizedSlState === normalizedLocState;
            const zipMatch = !normalizedLocZip || normalizedSlZip === normalizedLocZip;
            
            const match = addressMatch && cityMatch && stateMatch && zipMatch;
            
            if (match) {
              console.log(`     ✓ Found match by address:`, {
                id: sl.id,
                name: sl.name,
                address: sl.address,
                city: sl.city,
                state: sl.state,
                zip_code: sl.zip_code
              });
            }
            
            return match;
          });
          
          if (foundByAddress) {
            console.log(`   ✓ Found matching service location by address`);
            return { index, location: foundByAddress };
          } else {
            console.log(`   ✗ No matching service location found by address`);
          }
        } else {
          console.log(`   - No address to match against`);
        }
      }
      return { index, location: null };
    });
    
    const selectedLocationsResults = await Promise.all(selectedLocationsPromises);
    console.log('\n7. Selected locations results:', selectedLocationsResults);
    
    const selectedLocations = {};
    // Also update initialLocations with service_location_id if we found a match
    const updatedInitialLocations = [...initialLocations];
    
    selectedLocationsResults.forEach(({ index, location }) => {
      if (location) {
        selectedLocations[index] = location;
        // Update the service_location_id in initialLocations if we found a match
        updatedInitialLocations[index] = {
          ...updatedInitialLocations[index],
          service_location_id: location.id
        };
        console.log(`   Set selectedLocations[${index}]:`, {
          id: location.id,
          name: location.name,
          address: location.address
        });
        console.log(`   Updated initialLocations[${index}].service_location_id to: ${location.id}`);
      } else {
        console.log(`   No location for index ${index}`);
      }
    });
    
    console.log('\n8. Final selectedLocations object:', selectedLocations);
    console.log('9. Updated initialLocations:', updatedInitialLocations);
    console.log('10. Setting state...');
    console.log('   - editingTransportationLocations:', updatedInitialLocations);
    console.log('   - selectedServiceLocations:', selectedLocations);
    
    // Set all state at once
    setEditingTransportationLocations(updatedInitialLocations);
    setSelectedServiceLocations(selectedLocations);
    setIsEditingTransportationLocations(true);
    
    console.log('11. State set, edit mode enabled');
    console.log('=== END EDITING TRANSPORTATION LOCATIONS ===\n');
  };

  // Cancel editing transportation locations
  const cancelEditingTransportationLocations = () => {
    setIsEditingTransportationLocations(false);
    setEditingTransportationLocations([]);
    setSelectedServiceLocations({});
  };

  // Handle service location selection for transportation locations
  const handleTransportationServiceLocationSelect = (index, location) => {
    setSelectedServiceLocations(prev => ({
      ...prev,
      [index]: location
    }));
    
    // Update the location data with service location info
    setEditingTransportationLocations(prev => {
      const updated = [...prev];
      if (location) {
        // Auto-populate address fields from service location
        updated[index] = {
          ...updated[index],
          service_location_id: location.id,
          address: location.address || '',
          city: location.city || '',
          state: location.state || '',
          zip_code: location.zip_code || ''
        };
      } else {
        // Clear service location and keep existing address fields
        updated[index] = {
          ...updated[index],
          service_location_id: null
        };
      }
      return updated;
    });
  };

  // Handle transportation location change
  const handleTransportationLocationChange = (index, field, value) => {
    setEditingTransportationLocations(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  // Save transportation locations
  const saveTransportationLocations = async () => {
    try {
      setSaving(true);

      // Validate all locations have required fields (service_location_id or address)
      for (let i = 0; i < editingTransportationLocations.length; i++) {
        const location = editingTransportationLocations[i];
        if (!location.service_location_id && !location.address) {
          toast.error(`Please select a service location or enter an address for ${location.location_type} location (Leg ${location.leg_sequence})`);
          return;
        }
        if (!location.location_type || !location.leg_sequence) {
          toast.error('Location type and leg sequence are required');
          return;
        }
      }

      // Prepare locations for API - include service_location_id if selected
      const locationsToSave = editingTransportationLocations.map(loc => ({
        location_type: loc.location_type,
        leg_sequence: loc.leg_sequence,
        address: loc.address || '',
        city: loc.city || '',
        state: loc.state || '',
        zip_code: loc.zip_code || '',
        special_instructions: loc.special_instructions || '',
        service_location_id: loc.service_location_id || null
      }));

      const data = await makeAuthenticatedRequest(`/transportation-jobs/${id}/locations`, {
        method: 'PUT',
        body: JSON.stringify({
          locations: locationsToSave
        })
      });

      if (data.success) {
        // Reload appointment details to get updated locations
        await loadAppointmentDetails();
        setIsEditingTransportationLocations(false);
        setEditingTransportationLocations([]);
        setSelectedServiceLocations({});
        toast.success('Transportation locations updated successfully');
      } else {
        toast.error(data.message || 'Failed to update transportation locations');
      }
    } catch (error) {
      console.error('Error updating transportation locations:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to update transportation locations'));
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Appointment Not Found</h2>
          <button
            onClick={() => navigateBack(navigate, location.pathname, '/appointments')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-md border border-blue-100 p-8">
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-start space-x-4">
                <button
                  onClick={() => navigateBack(navigate, location.pathname, '/appointments')}
                  className="p-2.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
                  title="Go back"
                >
                  <ArrowLeftIcon className="h-6 w-6" />
                </button>
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-3 flex items-center">
                    {appointment.job_type === 'transportation' && (
                      <TruckIcon className="h-9 w-9 mr-3 text-blue-600" />
                    )}
                    {appointment.job_number || appointment.title}
                  </h1>
                  <div className="flex items-center space-x-5 text-base">
                    <span className="flex items-center text-gray-700 font-medium">
                      <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
                      {formatDate(appointment.scheduled_date)}
                    </span>
                    <span className="flex items-center text-gray-700 font-medium">
                      <ClockIcon className="h-5 w-5 mr-2 text-blue-600" />
                      {formatTime(appointment.scheduled_time)}
                    </span>
                    {appointment.job_type === 'transportation' && (
                      <span className="flex items-center text-purple-700 font-semibold">
                        <TruckIcon className="h-5 w-5 mr-2" />
                        Transportation Service
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-base font-semibold border ${
                isCancelledUnder24Hours(appointment)
                  ? 'bg-amber-100 text-amber-900 border-amber-300'
                  : appointment.status === 'completed'
                    ? 'bg-green-100 text-green-800 border-green-200'
                    : appointment.status === 'assigned'
                      ? 'bg-blue-100 text-blue-800 border-blue-200'
                      : appointment.status === 'finding_interpreter'
                        ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                        : 'bg-gray-100 text-gray-800 border-gray-200'
              }`}>
                {getJobStatusLabel(appointment)}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowChat(true)}
                className="relative bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-50 flex items-center text-sm font-medium shadow-sm transition-colors"
              >
                <ChatBubbleOvalLeftEllipsisIcon className="h-5 w-5 mr-2" />
                Chat Support
                {(() => {
                  const conv = conversations?.find(c => c.job_id === id);
                  const unread = conv ? (unreadByConversation[conv.id] || 0) : 0;
                  return unread > 0 ? (
                    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-xs px-2 py-0.5 min-w-[20px]">
                      {unread}
                    </span>
                  ) : null;
                })()}
              </button>
              {canEditAppointment(appointment) && !isEditing && (
                <>
                  <button
                    onClick={startEditing}
                    className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 flex items-center text-sm font-medium shadow-sm"
                  >
                    <PencilIcon className="h-5 w-5 mr-2" />
                    Edit Appointment
                  </button>
                  <button
                    onClick={cancelAppointment}
                    disabled={saving}
                    className="bg-red-600 text-white px-4 py-2.5 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center text-sm font-medium shadow-sm"
                  >
                    <XMarkIcon className="h-5 w-5 mr-2" />
                    {saving ? 'Cancelling...' : 'Cancel Appointment'}
                  </button>
                </>
              )}
              {canAuthorizeAppointment(appointment) && (
                <button
                  onClick={handleAuthorizeAppointment}
                  disabled={saving}
                  className="bg-amber-600 text-white px-4 py-2.5 rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center text-sm font-medium shadow-sm"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  {saving ? 'Authorizing...' : 'Authorize Appointment'}
                </button>
              )}
              {canReRequestJob(mapStatusForCustomer(appointment.status)) && (
                <button
                  onClick={() => navigate(`/appointments/new?reRequest=${appointment.id}&returnTo=/appointments/${appointment.id}`)}
                  className="bg-green-600 text-white px-4 py-2.5 rounded-lg hover:bg-green-700 flex items-center text-sm font-medium shadow-sm"
                >
                  <ArrowPathIcon className="h-5 w-5 mr-2" />
                  Re-request Appointment
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Cancelled under 24 hours notice */}
        {isCancelledUnder24Hours(appointment) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="mb-8 bg-amber-50 border-2 border-amber-300 rounded-xl shadow-lg p-6"
          >
            <div className="flex items-start">
              <ClockIcon className="h-8 w-8 text-amber-600 flex-shrink-0" />
              <div className="ml-4">
                <h3 className="text-xl font-bold text-amber-900 mb-2">
                  Cancelled Under 24 Hours
                </h3>
                <p className="text-amber-800">
                  {getCustomerStatusMessage(appointment)}
                  {appointment.cancelled_at && (
                    <span className="block mt-2 text-sm text-amber-700">
                      Cancelled on {formatDate(appointment.cancelled_at)}
                      {appointment.cancellation_reason ? ` — ${appointment.cancellation_reason}` : ''}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Transportation Authorization Section */}
        {appointment.job_type === 'transportation' && 
         appointment.status === 'pending_authorization' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8 bg-yellow-50 border-2 border-yellow-300 rounded-xl shadow-lg p-6"
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-xl font-bold text-yellow-900 mb-2">
                  Authorization Required
                </h3>
                <p className="text-yellow-800 mb-4">
                  Please review the transportation details and billing information below, then authorize or decline this service.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleAuthorizeTransportation}
                    disabled={saving}
                    className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
                  >
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    {saving ? 'Authorizing...' : 'Authorize Transportation'}
                  </button>
                  <button
                    onClick={handleDeclineTransportation}
                    disabled={saving}
                    className="inline-flex items-center px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
                  >
                    <XCircleIcon className="h-5 w-5 mr-2" />
                    Decline Transportation
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Enhanced Appointment Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <div className="bg-blue-600 p-2 rounded-lg mr-3">
                      <CalendarIcon className="h-6 w-6 text-white" />
                    </div>
                    Appointment Information
                  </h2>
                {isEditing && (
                  <div className="flex space-x-2">
                    <button
                      onClick={cancelEditing}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 flex items-center"
                    >
                      <XMarkIcon className="h-4 w-4 mr-1" />
                      Cancel
                    </button>
                    <button
                      onClick={saveChanges}
                      disabled={saving}
                      className="px-4 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
              </div>
              
              {isEditing ? (
                <div className="p-6 space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Editing Appointment
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>You can edit appointment details including date, time, location, and notes. When you save changes, notifications will be sent to our team and the assigned interpreter (if any) for confirmation.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Appointment Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={editData.appointmentDate || ''}
                        onChange={(e) => handleInputChange('appointmentDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Current: {formatDate(appointment.scheduled_date)}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={editData.startTime || ''}
                        onChange={(e) => handleInputChange('startTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Current: {formatTime(appointment.scheduled_time)}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Arrival Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        value={editData.arrivalTime || ''}
                        onChange={(e) => handleInputChange('arrivalTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Current: {appointment.arrival_time ? formatTime(appointment.arrival_time) : 'Not set'}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reserve Hours
                      </label>
                      <input
                        type="number"
                        min={getMinimumReserveHours(
                          resolveServiceTypeForReserveRules(
                            editData.serviceTypeId || appointment.service_type_id,
                            formOptions.serviceTypes,
                            editData.serviceType
                          )
                        )}
                        max="12"
                        step="0.5"
                        value={editData.reserveHours || ''}
                        onChange={(e) => handleReserveHoursChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Leave blank for default (${getMinimumReserveHours(
                          resolveServiceTypeForReserveRules(
                            editData.serviceTypeId || appointment.service_type_id,
                            formOptions.serviceTypes,
                            editData.serviceType
                          )
                        )} hours)`}
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Current: {calculateReserveHours(appointment.estimated_duration_minutes)} hours
                      </p>
                      <div className="mt-1 text-xs text-gray-500">
                        Minimum {getMinimumReserveHours(
                          resolveServiceTypeForReserveRules(
                            editData.serviceTypeId || appointment.service_type_id,
                            formOptions.serviceTypes,
                            editData.serviceType
                          )
                        )} hours for this service type. Leave blank to use the default.
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Calculated End Time
                      </label>
                      <input
                        type="text"
                        value={editData.startTime && editData.reserveHours ? calculateEndTime(editData.startTime, editData.reserveHours) : ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                        readOnly
                        placeholder="Will be calculated automatically"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Current: {formatTime(appointment.end_time)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Location Fields - Only show for non-transportation jobs */}
                  {appointment.job_type !== 'transportation' && (
                    <>
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <MapPinIcon className="h-5 w-5 mr-2 text-gray-700" />
                          Location Information
                        </h3>
                        
                        <div className="space-y-6">
                          {/* Service Location Selector */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Service Location <span className="text-red-500">*</span>
                            </label>
                            <ServiceLocationSelector
                              selectedLocation={editData.selectedServiceLocation}
                              onLocationSelect={handleServiceLocationSelect}
                              placeholder="Select a service location or add a new one"
                              showAddNew={true}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              💡 Select from existing locations or add a new one.
                            </p>
                          </div>

                          {/* Location Contact Selection - Show only if service location is selected */}
                          {editData.selectedServiceLocation && editData.serviceLocationId && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <label className="block text-sm font-medium text-gray-700">
                                  Doctor Name <span className="text-red-500">*</span>
                                </label>
                                <button
                                  type="button"
                                  onClick={() => setShowNewContactModal(true)}
                                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center font-medium"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add New Contact
                                </button>
                              </div>
                              
                              {locationContacts && locationContacts.length > 0 ? (
                                <>
                                  <SearchableSelect
                                    value={editData.locationContactId || ''}
                                    onChange={(e) => handleLocationContactSelect(e.target.value)}
                                    onClear={() => handleLocationContactSelect('')}
                                    options={locationContacts.map(contact => ({
                                      value: contact.id.toString(),
                                      label: `${contact.name}${contact.title ? ` - ${contact.title}` : ''}`
                                    }))}
                                    placeholder="Search location contact..."
                                    required
                                  />
                                  <p className="mt-2 text-xs text-gray-600">
                                    💡 Please select a contact person for this location or add a new one using the button above.
                                  </p>
                                </>
                              ) : (
                                <div className="text-center py-4">
                                  <p className="text-sm text-gray-700 mb-3">
                                    No location contacts found for this service location.
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => setShowNewContactModal(true)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
                                  >
                                    <PlusIcon className="h-4 w-4 mr-2" />
                                    Add First Location Contact
                                  </button>
                                  <p className="mt-2 text-xs text-gray-600">
                                    💡 You must add at least one location contact to continue.
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
                        
                        <div className="grid grid-cols-1 gap-6">
                          <div>
                            <SearchableSelect
                              label="Appointment Type"
                              value={editData.appointmentType || ''}
                              onChange={(e) => handleInputChange('appointmentType', e.target.value)}
                              onClear={() => handleInputChange('appointmentType', '')}
                              options={appointmentTypeOptions}
                              placeholder="Search appointment type..."
                              required
                            />
                            <p className="mt-1 text-sm text-gray-500">
                              Current: {appointment.appointment_type ? appointment.appointment_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not set'}
                            </p>
                          </div>
                          
                          {/* Service Type - Only for non-transportation jobs */}
                          {appointment.job_type !== 'transportation' && (
                            <div>
                              <SearchableSelect
                                label="Service Type"
                                value={editData.serviceType || ''}
                                onChange={(e) => handleInputChange('serviceType', e.target.value)}
                                onClear={() => handleInputChange('serviceType', '')}
                                options={serviceTypeOptions}
                                placeholder="Search service type..."
                                required
                              />
                              <p className="mt-1 text-sm text-gray-500">
                                Current: {appointment.service_type_name || 'Not set'}
                              </p>
                            </div>
                          )}
                          
                          {/* Meeting Link - Show only when service type is video */}
                          {editData.serviceType === 'video' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Meeting Link (Optional)
                              </label>
                              <input
                                type="url"
                                value={editData.meetingLink || ''}
                                onChange={(e) => handleInputChange('meetingLink', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., https://zoom.us/j/123456789 or https://meet.google.com/abc-defg-hij"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                💡 If you have a specific meeting link (Zoom, Google Meet, etc.), you can provide it here.
                              </p>
                              <p className="mt-1 text-sm text-gray-500">
                                Current: {appointment.meeting_link || 'Not set'}
                              </p>
                            </div>
                          )}
                          
                          {/* Interpreter Type - Show only for onsite (non-remote) appointments */}
                          {appointment.job_type !== 'transportation' && !appointment.is_remote && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Interpreter Type
                                {(() => {
                                  const category = editData.appointmentType ? getAppointmentCategory(editData.appointmentType) : null;
                                  const needsInterpreterType = category === 'medical-standard';
                                  return needsInterpreterType && <span className="text-red-500">*</span>;
                                })()}
                              </label>
                              <select
                                value={editData.interpreterType || ''}
                                onChange={(e) => handleInputChange('interpreterType', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={(() => {
                                  const category = editData.appointmentType ? getAppointmentCategory(editData.appointmentType) : null;
                                  return category === 'legal' || category === 'medical-legal';
                                })()}
                              >
                                <option value="">Select interpreter type</option>
                                {formOptions.interpreterTypes.map(type => (
                                  <option key={type.id} value={type.id.toString()}>
                                    {type.name}
                                  </option>
                                ))}
                              </select>
                              <p className="mt-1 text-sm text-gray-500">
                                {(() => {
                                  const category = editData.appointmentType ? getAppointmentCategory(editData.appointmentType) : null;
                                  if (category === 'legal' || category === 'medical-legal') {
                                    return 'Interpreter type is automatically set based on appointment type';
                                  }
                                  if (category === 'medical-standard') {
                                    return 'Please select an interpreter type';
                                  }
                                  return `Current: ${appointment.interpreter_type_name || 'Not set'}`;
                                })()}
                              </p>
                            </div>
                          )}
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Appointment Notes
                            </label>
                            <textarea
                              value={editData.appointmentNotes || ''}
                              onChange={(e) => setEditData(prev => ({ ...prev, appointmentNotes: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              rows="3"
                              placeholder="Any additional notes or instructions"
                            />
                            <p className="mt-1 text-sm text-gray-500">
                              Current: {appointment.appointment_notes || 'Not set'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Date & Time combined */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Date & Time</label>
                    <div className="flex items-start space-x-3">
                      <CalendarIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">{formatDate(appointment.scheduled_date)}</p>
                        <p className="text-gray-900 mt-1">{formatTime(appointment.scheduled_time)}</p>
                        {appointment.arrival_time && (
                          <p className="text-sm text-blue-600 mt-1">
                            Arrive by: {formatTime(appointment.arrival_time)}
                          </p>
                        )}
                        {appointment.job_type === 'transportation' && appointment.pickup_time && (
                          <p className="text-sm text-green-600 mt-1">
                            Pickup: {formatTime(appointment.pickup_time)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  
                  {/* Transportation Details */}
                  {appointment.job_type === 'transportation' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Service Type</label>
                        <div className="flex items-center space-x-3">
                          <TruckIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                          <p className="font-semibold text-gray-900 capitalize">
                            {appointment.transportation_service_type 
                              ? appointment.transportation_service_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                              : 'Not specified'}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Trip Type</label>
                        <div className="flex items-center space-x-3">
                          <TruckIcon className="h-5 w-5 text-purple-600 flex-shrink-0" />
                          <p className="font-semibold text-gray-900 capitalize">
                            {appointment.trip_type ? appointment.trip_type.replace('_', ' ') : 'Not specified'}
                          </p>
                        </div>
                      </div>
                      
                      {(appointment.wait_time || appointment.trip_type === 'round_trip_wait') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-2">Wait Time</label>
                          <div className="flex items-start space-x-3">
                            <ClockIcon className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-semibold text-gray-900">
                                {appointment.wait_time ? `${appointment.wait_time} minutes` : 
                                 appointment.trip_type === 'round_trip_wait' ? 'TBD' : 'No wait time'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Interpreting Duration */}
                  {appointment.job_type !== 'transportation' && appointment.estimated_duration_minutes && (
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Duration</label>
                      <div className="flex items-start space-x-3">
                        <ClockIcon className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900">
                            {appointment.estimated_duration_minutes} minutes ({(appointment.estimated_duration_minutes / 60).toFixed(1)} hours)
                          </p>
                          {appointment.actual_duration_minutes && (
                            <p className="text-sm text-green-600 mt-1">
                              Actual: {appointment.actual_duration_minutes} min
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Appointment Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Appointment Type</label>
                    <div className="flex items-center space-x-3">
                      <DocumentTextIcon className="h-5 w-5 text-purple-600 flex-shrink-0" />
                      <p className="font-semibold text-gray-900">{appointment.appointment_type || 'Not specified'}</p>
                    </div>
                  </div>
                  
                  {/* Service Type & Language for interpreting jobs */}
                  {appointment.job_type !== 'transportation' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Service Type</label>
                        <div className="flex items-center space-x-3">
                          <DocumentTextIcon className="h-5 w-5 text-purple-600 flex-shrink-0" />
                          <p className="font-semibold text-gray-900">{appointment.service_type_name || 'Not specified'}</p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Language</label>
                        <div className="flex items-center space-x-3">
                          <LanguageIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                          <p className="font-semibold text-gray-900">{appointment.language_name || 'Not specified'}</p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Interpreter Type</label>
                        <div className="flex items-center space-x-3">
                          <UserIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <p className="font-semibold text-gray-900">{appointment.interpreter_type_name || 'Not specified'}</p>
                        </div>
                      </div>
                      
                      {/* Meeting Link - Show for remote/video appointments */}
                      {(appointment.is_remote || appointment.service_type_name?.toLowerCase().includes('video') || appointment.service_type_name?.toLowerCase().includes('remote')) && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-600 mb-2">Meeting Link</label>
                          <div className="flex items-start space-x-3">
                            <svg className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                              <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                            </svg>
                            {appointment.meeting_link ? (
                              <a 
                                href={appointment.meeting_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="font-semibold text-blue-600 hover:text-blue-800 underline break-all"
                              >
                                {appointment.meeting_link}
                              </a>
                            ) : (
                              <p className="font-semibold text-gray-500">Not provided</p>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Appointment Notes */}
                  {appointment.appointment_notes && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Notes</label>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{appointment.appointment_notes}</p>
                    </div>
                  )}
                </div>
              </div>
              )}

            </motion.div>

            {/* Authorization Status Section */}
            {appointment.customer_authorization_required && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
              >
                <div className={`px-6 py-4 border-b ${
                  appointment.customer_authorization_obtained 
                    ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-100' 
                    : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-100'
                }`}>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <div className={`p-2 rounded-lg mr-3 ${
                      appointment.customer_authorization_obtained 
                        ? 'bg-green-600' 
                        : 'bg-amber-600'
                    }`}>
                      {appointment.customer_authorization_obtained ? (
                        <CheckCircleIcon className="h-6 w-6 text-white" />
                      ) : (
                        <ClockIcon className="h-6 w-6 text-white" />
                      )}
                    </div>
                    Authorization Status
                  </h2>
                </div>
                
                <div className="p-6">
                  {appointment.customer_authorization_obtained ? (
                    <div className="space-y-4">
                      <div className="flex items-center p-4 bg-green-50 rounded-lg border border-green-200">
                        <CheckCircleIcon className="h-8 w-8 text-green-600 mr-3" />
                        <div>
                          <p className="text-lg font-semibold text-green-900">Authorized</p>
                          <p className="text-sm text-green-700">This appointment has been authorized</p>
                        </div>
                      </div>
                      
                      {appointment.authorized_by_name && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600">Authorized by</p>
                          <p className="text-lg font-semibold text-gray-900">{appointment.authorized_by_name}</p>
                          {appointment.authorized_at && (
                            <p className="text-sm text-gray-600 mt-1">
                              {new Date(appointment.authorized_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <ClockIcon className="h-8 w-8 text-amber-600 mr-3" />
                        <div>
                          <p className="text-lg font-semibold text-amber-900">Awaiting Authorization</p>
                          <p className="text-sm text-amber-700">
                            {appointment.can_authorize 
                              ? 'You can authorize this appointment using the button above'
                              : 'This appointment requires authorization from the claim contact handler or adjuster\'s assistant'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Enhanced Location Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <div className="bg-green-600 p-2 rounded-lg mr-3">
                      <MapPinIcon className="h-6 w-6 text-white" />
                    </div>
                    Location
                  </h2>
                  {appointment.job_type === 'transportation' && (
                    <div className="flex items-center gap-4">
                      {calculatingMiles ? (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                          <span className="text-sm text-gray-600">Calculating...</span>
                        </div>
                      ) : totalMiles ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-semibold text-orange-600">{totalMiles}</span>
                          <span className="text-sm text-gray-600">miles</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => calculateTotalMiles(true)}
                          className="bg-orange-600 text-white px-3 py-1.5 rounded-lg hover:bg-orange-700 flex items-center text-sm"
                        >
                          <svg className="h-3 w-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Calculate Distance
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {isEditing ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-gray-800">
                        {appointment.job_type === 'transportation' 
                          ? 'Transportation Location Not Editable Here'
                          : 'Edit Location Above'}
                      </h3>
                      <div className="mt-2 text-sm text-gray-600">
                        <p>
                          {appointment.job_type === 'transportation' 
                            ? 'Transportation pickup/dropoff locations cannot be changed. If you need to change locations, please cancel this appointment and create a new one.'
                            : 'Location fields can be edited in the form above. Changes will be saved when you click "Save Changes".'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  {appointment.job_type === 'transportation' ? (
                    <div className="space-y-6">
                      {/* Edit button */}
                      {!isEditingTransportationLocations && (
                        <div className="flex justify-end">
                          <button
                            onClick={startEditingTransportationLocations}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <PencilIcon className="h-4 w-4 mr-2" />
                            Edit Locations
                          </button>
                        </div>
                      )}
                      {totalMiles && (
                        <div className="mb-4 p-3 bg-orange-50 rounded-lg">
                          <p className="text-xs text-orange-800">
                            <strong>Note:</strong> Distance is calculated using Google Maps driving directions and rounded up to the nearest mile.
                          </p>
                        </div>
                      )}

                      {/* Edit form or display */}
                      {isEditingTransportationLocations ? (
                        <div className="space-y-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <p className="text-sm text-blue-800">
                              You can edit transportation job locations. When you save changes, notifications will be sent to our team.
                            </p>
                          </div>
                          {editingTransportationLocations.map((location, index) => (
                            <div key={index} className={`border rounded-lg p-4 ${
                              location.location_type === 'pickup' 
                                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                                : location.location_type === 'dropoff'
                                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                                : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
                            }`}>
                              <div className="flex items-start mb-4">
                                <div className={`p-2 rounded-lg mr-4 mt-1 flex-shrink-0 ${
                                  location.location_type === 'pickup' 
                                    ? 'bg-green-600'
                                    : location.location_type === 'dropoff'
                                    ? 'bg-blue-600'
                                    : 'bg-purple-600'
                                }`}>
                                  <MapPinIcon className="h-5 w-5 text-white" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <span className={`text-white text-xs px-2 py-1 rounded-full mr-2 ${
                                      location.location_type === 'pickup' 
                                        ? 'bg-green-600'
                                        : location.location_type === 'dropoff'
                                        ? 'bg-blue-600'
                                        : 'bg-purple-600'
                                    }`}>
                                      {location.location_type === 'pickup' ? 'PICKUP' : 
                                       location.location_type === 'dropoff' ? 'DROPOFF' : 'STOP'}
                                    </span>
                                    {location.location_type === 'pickup' 
                                      ? 'Pickup Location'
                                      : location.location_type === 'dropoff'
                                      ? 'Dropoff Location'
                                      : 'Stop Location'
                                    } - Step {location.leg_sequence}
                                  </h3>
                                  <div className="space-y-3">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Service Location <span className="text-red-500">*</span>
                                      </label>
                                      {(() => {
                                        const selectedLoc = selectedServiceLocations[index];
                                        console.log(`[DEBUG] Rendering ServiceLocationSelector for location ${index}:`, {
                                          index,
                                          selectedLocation: selectedLoc,
                                          selectedLocationId: selectedLoc?.id,
                                          locationData: location,
                                          locationServiceLocationId: location.service_location_id,
                                          allSelectedLocations: selectedServiceLocations
                                        });
                                        return null;
                                      })()}
                                      <ServiceLocationSelector
                                        selectedLocation={selectedServiceLocations[index] || null}
                                        onLocationSelect={(location) => handleTransportationServiceLocationSelect(index, location)}
                                        placeholder="Select a service location"
                                        showAddNew={true}
                                      />
                                      {selectedServiceLocations[index] ? (
                                        <p className="text-xs text-gray-500 mt-1">
                                          Selected: {selectedServiceLocations[index].name} - {selectedServiceLocations[index].address}
                                        </p>
                                      ) : location.service_location_id ? (
                                        <p className="text-xs text-amber-600 mt-1">
                                          Service location ID: {location.service_location_id} (not found in list)
                                        </p>
                                      ) : location.address ? (
                                        <p className="text-xs text-gray-500 mt-1">
                                          Address: {location.address}, {location.city}, {location.state} {location.zip_code}
                                        </p>
                                      ) : null}
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Special Instructions
                                      </label>
                                      <textarea
                                        value={location.special_instructions || ''}
                                        onChange={(e) => handleTransportationLocationChange(index, 'special_instructions', e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Any special instructions for this location..."
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className="flex justify-end space-x-3 pt-4">
                            <button
                              onClick={cancelEditingTransportationLocations}
                              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                              disabled={saving}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={saveTransportationLocations}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                              disabled={saving}
                            >
                              {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Transportation Locations and Route Map - Side by Side */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Transportation Locations */}
                            <div className="space-y-3">
                              <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-3">
                                <MapPinIcon className="h-5 w-5 text-orange-600 mr-2" />
                                Transportation Locations
                              </h3>
                              {appointment.transportation_locations && appointment.transportation_locations.length > 0 ? (
                                // New multi-trip format
                                appointment.transportation_locations.map((location, index) => (
                                  <div key={index} className={`border rounded-lg p-3 ${
                                    location.location_type === 'pickup' 
                                      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                                      : location.location_type === 'dropoff'
                                      ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                                      : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
                                  }`}>
                                    <div className="flex items-start">
                                      <div className={`p-2 rounded-lg mr-3 mt-1 flex-shrink-0 ${
                                        location.location_type === 'pickup' 
                                          ? 'bg-green-600'
                                          : location.location_type === 'dropoff'
                                          ? 'bg-blue-600'
                                          : 'bg-purple-600'
                                      }`}>
                                        <MapPinIcon className="h-4 w-4 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center flex-wrap gap-2 mb-1">
                                          <span className={`text-white text-xs px-2 py-0.5 rounded-full ${
                                            location.location_type === 'pickup' 
                                              ? 'bg-green-600'
                                              : location.location_type === 'dropoff'
                                              ? 'bg-blue-600'
                                              : 'bg-purple-600'
                                          }`}>
                                            {location.location_type === 'pickup' ? 'PICKUP' : 
                                             location.location_type === 'dropoff' ? 'DROPOFF' : 'STOP'}
                                          </span>
                                          <span className="text-sm text-gray-500">Step {location.leg_sequence}</span>
                                        </div>
                                        <p className="text-gray-900 font-medium text-sm">{location.address}</p>
                                        <p className="text-gray-600 text-xs">
                                          {location.city}, {location.state} {location.zip_code}
                                        </p>
                                        {location.special_instructions && (
                                          <p className="text-gray-500 text-xs mt-1 italic">
                                            {location.special_instructions}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                // Legacy format for backward compatibility
                                <>
                                  {/* Pickup Location */}
                                  {appointment.pickup_location && (
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
                                      <div className="flex items-start">
                                        <div className="bg-green-600 p-2 rounded-lg mr-3 mt-1 flex-shrink-0">
                                          <MapPinIcon className="h-4 w-4 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full">PICKUP</span>
                                          </div>
                                          <p className="text-gray-900 font-medium text-sm">{appointment.pickup_location.address}</p>
                                          <p className="text-gray-600 text-xs">
                                            {appointment.pickup_location.city}, {appointment.pickup_location.state} {appointment.pickup_location.zip_code}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Dropoff Location */}
                                  {appointment.dropoff_location && (
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                                      <div className="flex items-start">
                                        <div className="bg-blue-600 p-2 rounded-lg mr-3 mt-1 flex-shrink-0">
                                          <MapPinIcon className="h-4 w-4 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">DROPOFF</span>
                                          </div>
                                          <p className="text-gray-900 font-medium text-sm">{appointment.dropoff_location.address}</p>
                                          <p className="text-gray-600 text-xs">
                                            {appointment.dropoff_location.city}, {appointment.dropoff_location.state} {appointment.dropoff_location.zip_code}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Second Dropoff Location (for multi-leg trips) */}
                                  {appointment.second_dropoff_location && (
                                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3">
                                      <div className="flex items-start">
                                        <div className="bg-purple-600 p-2 rounded-lg mr-3 mt-1 flex-shrink-0">
                                          <MapPinIcon className="h-4 w-4 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">2ND DROPOFF</span>
                                          </div>
                                          <p className="text-gray-900 font-medium text-sm">{appointment.second_dropoff_location.address}</p>
                                          <p className="text-gray-600 text-xs">
                                            {appointment.second_dropoff_location.city}, {appointment.second_dropoff_location.state} {appointment.second_dropoff_location.zip_code}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            {/* Route Visualization Map */}
                            {(appointment.transportation_locations?.length > 0 || 
                              appointment.pickup_location || 
                              appointment.dropoff_location) && (
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                  <MapPinIcon className="h-5 w-5 text-blue-600 mr-2" />
                                  Route Visualization
                                </h3>
                                <TransportationRouteMap 
                                  locations={
                                    appointment.transportation_locations?.length > 0
                                      ? appointment.transportation_locations
                                      : [
                                          appointment.pickup_location && { ...appointment.pickup_location, location_type: 'pickup' },
                                          appointment.dropoff_location && { ...appointment.dropoff_location, location_type: 'dropoff' },
                                          appointment.second_dropoff_location && { ...appointment.second_dropoff_location, location_type: 'second_dropoff' }
                                        ].filter(Boolean)
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Show remote appointment notice if it's a remote appointment */}
                      {appointment.is_remote && (
                        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl p-4 mb-4">
                          <div className="flex items-center">
                            <div className="bg-white/20 p-2 rounded-full mr-3">
                              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-bold">Remote Appointment</h3>
                              <p className="text-blue-100 text-sm">
                                This appointment will be conducted remotely via video or phone.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Always show location information */}
                      <div className="flex items-start bg-gray-50 rounded-lg p-4">
                        <MapPinIcon className="h-6 w-6 text-green-600 mr-4 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          {appointment.service_location_name && (
                            <p className="text-lg font-bold text-gray-900 mb-1">{appointment.service_location_name}</p>
                          )}
                          {(appointment.service_location_address || appointment.location_address) && (
                            <p className={`${appointment.service_location_name ? 'text-gray-700' : 'text-lg font-semibold text-gray-900'} mb-0.5`}>
                              {appointment.service_location_address || appointment.location_address}
                            </p>
                          )}
                          {appointment.service_location_address_2 && (
                            <p className="text-gray-700 mb-0.5">{appointment.service_location_address_2}</p>
                          )}
                          {((appointment.service_location_city || appointment.location_city) && (appointment.service_location_state || appointment.location_state)) && (
                            <p className="text-gray-600">
                              {appointment.service_location_city || appointment.location_city}, {appointment.service_location_state || appointment.location_state}
                              {(appointment.service_location_zip_code || appointment.location_zip_code) && ` ${appointment.service_location_zip_code || appointment.location_zip_code}`}
                            </p>
                          )}
                          {!appointment.service_location_address && !appointment.location_address && !appointment.service_location_city && !appointment.location_city && (
                            <p className="text-gray-500 italic">Location information not available</p>
                          )}
                          {(appointment.location_contact_name || appointment.location_contact_phone) && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-sm font-medium text-gray-700 mb-1">Doctor Name</p>
                              {appointment.location_contact_name && (
                                <p className="text-sm text-gray-900">{appointment.location_contact_name}</p>
                              )}
                              {appointment.location_contact_phone && (
                                <p className="text-sm text-gray-600">{appointment.location_contact_phone}</p>
                              )}
                            </div>
                          )}
                          {appointment.facility_phone && !appointment.location_contact_phone && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-sm font-medium text-gray-700 mb-1">Facility Phone</p>
                              <p className="text-sm text-gray-900">{appointment.facility_phone}</p>
                            </div>
                          )}
                          {!appointment.is_remote && (
                            <button className="mt-3 inline-flex items-center text-sm text-green-600 hover:text-green-700 font-medium">
                              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                              </svg>
                              Get Directions
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            {/* Related Appointments Section */}
            {relatedAppointments.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.12 }}
                className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-4 border-b border-purple-100">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <div className="bg-purple-600 p-2 rounded-lg mr-3">
                      <UserGroupIcon className="h-6 w-6 text-white" />
                    </div>
                    Related Appointments
                  </h2>
                </div>
                <div className="p-6">
                  {loadingRelatedAppointments ? (
                    <p className="text-sm text-gray-500">Loading related appointments...</p>
                  ) : relatedAppointments.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {relatedAppointments.map((relatedAppointment) => (
                        <div
                          key={relatedAppointment.id}
                          onClick={() => navigate(`/appointments/${relatedAppointment.id}`)}
                          className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors"
                        >
                          <div className="flex flex-col h-full">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium text-gray-900 text-sm">
                                {relatedAppointment.job_number || relatedAppointment.title}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getJobStatusColor(relatedAppointment)}`}>
                                {getJobStatusLabel(relatedAppointment)}
                              </span>
                            </div>
                            <div className="flex flex-col text-xs text-gray-600 gap-2 flex-grow">
                              <div className="flex items-center">
                                <CalendarIcon className="h-3 w-3 mr-1" />
                                {formatDate(relatedAppointment.scheduled_date)}
                              </div>
                              <div className="flex items-center">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                {formatTime(relatedAppointment.scheduled_time)}
                              </div>
                              {relatedAppointment.arrival_time && (
                                <div className="flex items-center text-xs text-gray-500">
                                  Arrive: {formatTime(relatedAppointment.arrival_time)}
                                </div>
                              )}
                            </div>
                            {relatedAppointment.appointment_type && (
                              <p className="text-xs text-gray-500 mt-2">
                                {relatedAppointment.appointment_type.replace(/_/g, ' ')}
                              </p>
                            )}
                            <div className="mt-auto pt-2 flex justify-end">
                              <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </motion.div>
            )}


            {/* Removed Service Details section for interpreter jobs; moved fields into Appointment Information */}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Enhanced Claimant Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 border-b border-indigo-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center">
                  <div className="bg-indigo-600 p-2 rounded-lg mr-3">
                    <UserIcon className="h-5 w-5 text-white" />
                  </div>
                  Claimant & Claim Information
                </h2>
              </div>
              
              <div className="p-6 space-y-3">
                {/* Claimant Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Name</label>
                  <div className="flex items-center space-x-2">
                    <UserIcon className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                    <p className="font-semibold text-gray-900">
                      {appointment.claimant_first_name} {appointment.claimant_last_name}
                    </p>
                  </div>
                </div>
                
                {/* Claimant Phone */}
                {appointment.claimant_phone && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Phone</label>
                    <div className="flex items-center space-x-2">
                      <PhoneIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <p className="font-semibold text-gray-900">{appointment.claimant_phone}</p>
                    </div>
                  </div>
                )}
                
                {/* Claimant Language */}
                {appointment.claimant_language && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Language</label>
                    <div className="flex items-center space-x-2">
                      <LanguageIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <p className="font-semibold text-gray-900">{appointment.claimant_language}</p>
                    </div>
                  </div>
                )}
                
                {/* Claimant Address */}
                {appointment.claimant_address && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Address</label>
                    <div className="flex items-start space-x-2">
                      <MapPinIcon className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="font-semibold text-gray-900">{appointment.claimant_address}</p>
                    </div>
                  </div>
                )}

                {/* Claim Information */}
                {appointment.claim_number && (
                  <>
                    <div className="border-t border-gray-200 my-3 pt-3"></div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Claim Number</label>
                      <div className="flex items-center space-x-2">
                        <DocumentTextIcon className="h-4 w-4 text-orange-600 flex-shrink-0" />
                        <p className="font-semibold text-gray-900">{appointment.claim_number}</p>
                      </div>
                    </div>
                  </>
                )}
                
                {appointment.case_type && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Case Type</label>
                    <div className="flex items-center space-x-2">
                      <DocumentTextIcon className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <p className="font-semibold text-gray-900">{appointment.case_type}</p>
                    </div>
                  </div>
                )}
                
                {appointment.date_of_injury && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Date of Injury</label>
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="h-4 w-4 text-red-600 flex-shrink-0" />
                      <p className="font-semibold text-gray-900">
                        {formatDate(appointment.date_of_injury)}
                      </p>
                    </div>
                  </div>
                )}
                
                {appointment.diagnosis && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Diagnosis</label>
                    <div className="flex items-start space-x-2">
                      <DocumentTextIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="font-semibold text-gray-900">{appointment.diagnosis}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>


            {/* Enhanced Interpreter Information - show for all non-transportation jobs when assigned */}
            {appointment.job_type !== 'transportation' && appointment.interpreter_first_name && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-100">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center">
                    <div className="bg-green-600 p-2 rounded-lg mr-3">
                      <UserIcon className="h-5 w-5 text-white" />
                    </div>
                    Assigned Interpreter
                  </h2>
                </div>
                
                <div className="p-6 space-y-3">
                  {/* Interpreter Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Name</label>
                    <div className="flex items-center space-x-2">
                      <UserIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <p className="font-semibold text-gray-900">
                        {appointment.interpreter_first_name} {appointment.interpreter_last_name}
                      </p>
                    </div>
                  </div>
                  
                  {/* Assignment Status */}
                  {appointment.assignment_status && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Assignment Status</label>
                      <div className="flex items-center space-x-2">
                        {appointment.assignment_status === 'accepted' ? (
                          <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                        ) : appointment.assignment_status === 'declined' ? (
                          <XCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0" />
                        ) : (
                          <ClockIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                        )}
                        <p className="font-semibold text-gray-900 capitalize">{appointment.assignment_status}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Medical Certification */}
                  {appointment.interpreter_type_code === 'medical_certified' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Medical Certification</label>
                      <div className="flex items-start space-x-2">
                        <DocumentTextIcon className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <p className="font-semibold text-gray-900">
                          {formatCertificationDetails(appointment.interpreter_medical_certification)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Court Certification */}
                  {appointment.interpreter_type_code === 'court_certified' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Court Certification</label>
                      <div className="flex items-start space-x-2">
                        <DocumentTextIcon className="h-4 w-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <p className="font-semibold text-gray-900">
                          {formatCertificationDetails(appointment.interpreter_court_certification)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Interpreter Bio */}
                  {appointment.interpreter_bio && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Bio</label>
                      <div className="flex items-start space-x-2">
                        <DocumentTextIcon className="h-4 w-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <p className="font-semibold text-gray-900">{appointment.interpreter_bio}</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}


            {/* Invoice Section - Show only if status is 'billed' */}
            {appointment.status === 'billed' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-white rounded-lg shadow p-6"
              >
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-green-600" />
                  Invoice
                </h2>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Status</label>
                    <p className="text-sm text-gray-900">Invoice has been generated and sent</p>
                  </div>
                  
                  <div className="pt-3">
                    <button
                      onClick={async () => {
                        try {
                          // Make authenticated request to get invoice PDF
                          const invoiceUrl = `${API_BASE}/customer/appointments/${appointment.id}/invoice`;
                          const response = await makeAuthenticatedRequest(invoiceUrl);
                          
                          if (response.ok) {
                            // Create blob from response and open in new tab
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            
                            // Clean up the URL after a delay
                            setTimeout(() => {
                              window.URL.revokeObjectURL(url);
                            }, 1000);
                          } else {
                            toast.error('Failed to load invoice. Please try again.');
                          }
                        } catch (error) {
                          console.error('Error loading invoice:', error);
                          toast.error(getCustomerPortalErrorMessage(error, 'Failed to load invoice. Please try again.'));
                        }
                      }}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center text-sm"
                    >
                      <DocumentTextIcon className="h-4 w-4 mr-2" />
                      View Invoice PDF
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
          </div>
        </div>
        
        {/* Add New Location Contact Modal */}
        {showNewContactModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Add New Location Contact
                </h3>
                
                <form onSubmit={handleCreateLocationContact} onClick={(e) => e.stopPropagation()} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Doctor Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newContactFormData.name}
                      onChange={(e) => setNewContactFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Doctor name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email (Optional)
                    </label>
                    <input
                      type="email"
                      value={newContactFormData.email}
                      onChange={(e) => setNewContactFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="contact@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={newContactFormData.phone}
                      onChange={(e) => setNewContactFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    <select
                      value={newContactFormData.title}
                      onChange={(e) => setNewContactFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select title (optional)</option>
                      <option value="Doctor">Doctor</option>
                      <option value="Physician Assistant">Physician Assistant</option>
                      <option value="Nurse Practitioner">Nurse Practitioner</option>
                      <option value="Nurse">Nurse</option>
                      <option value="Doctor Assistant">Doctor Assistant</option>
                      <option value="Receptionist">Receptionist</option>
                      <option value="Office Coordinator">Office Coordinator</option>
                    </select>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewContactModal(false);
                        setNewContactFormData({
                          name: '',
                          email: '',
                          phone: '',
                          title: ''
                        });
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Create Contact
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        
        {/* Job Chat Widget */}
        <JobChatWidget 
          jobId={id}
          jobTitle={appointment.job_number || appointment.title}
          isOpen={showChat}
          onClose={() => setShowChat(false)}
        />

        <AdjusterConfirmModal
          isOpen={!!pendingClaim}
          claim={pendingClaim}
          onSave={saveAssignment}
          onSkip={skipPrompt}
          saving={savingRole}
        />
      </div>
    );
  };

export default AppointmentDetails;
